import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode, Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { FaChevronLeft, FaChevronRight, FaFireAlt, FaRegBookmark, FaShare, FaDollarSign, FaRupeeSign } from "react-icons/fa";
import { LuShoppingCart } from "react-icons/lu";
import { IoShareSocial } from "react-icons/io5";
import { Link } from "react-router-dom";
import { getProductById, getAllProduct, AddToCart } from "../API/ProductAPI";
import Config from "../API/Config";
import { toast } from "sonner";
import { PuffLoader } from "react-spinners";
import { useCart } from "../context/CartContext";

const ProductDetails = () => {
  const { Id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // Added error state
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false); // ✅ new state
  const { refreshCartCount } = useCart();


  const StateId = Config.getStateId();
  const CountryId = Config.getCountryId();
  const currencySymbol = CountryId === 'IN' ? <FaRupeeSign /> : <FaDollarSign />;

  // Fetch product details and related products
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch product by ID first
        const productRes = await getProductById(Id, CountryId, StateId);
        if (productRes.status) {
          setProduct(productRes.responseData);
          setSelectedVariation(productRes.responseData.Variations[0]);

        } else {
          throw new Error(productRes.message || 'Failed to fetch product');
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message || 'An error occurred while fetching product details.');
      } finally {
        setLoading(false);
      }
    };

    if (Id) {
      fetchData();
    }
  }, [Id, StateId, CountryId]);

  const increaseQty = () => setQuantity(quantity + 1);
  const decreaseQty = () => setQuantity(quantity > 1 ? quantity - 1 : 1);

  const handleVariationChange = (e) => {
    const variationId = parseInt(e.target.value);
    const variation = product.Variations.find(v => v.Id === variationId);
    setSelectedVariation(variation);
  };

  // Share functionality
  const handleShare = async () => {
    if (!product) return;
    const shareData = {
      title: product.Name,
      text: product.Description.replace(/<[^>]+>/g, ""), // Strip HTML tags from description
      url: window.location.href, // Current product page URL
    };

    try {
      if (navigator.share) {
        // Use Web Share API if available
        await navigator.share(shareData);
        console.log("Product shared successfully!");
      } else {
        // Fallback: Copy URL to clipboard
        await navigator.clipboard.writeText(shareData.url);
        alert("Product URL copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing product:", error);
    }
  };

  // Add to Cart functionality
  const handleAddToCart = async () => {
    setIsAddingToCart(true);

    if (!product || !selectedVariation) {
      toast.error("Product details not loaded.");
      return;
    }

    const isLoggedIn = Config.getUserIsLogin();

    if (!isLoggedIn) {
      // Show toast.info and redirect to sign-in after 2 seconds
      toast.info("You need to log in to add items to your cart.", {
        duration: 2000,
      });
      setTimeout(() => {
        sessionStorage.setItem("redirectAfterLogin", `/product-view/${Id}`);
        navigate("/sign-in");
      }, 2000);
      return;
    }

    // If logged in, call AddToCart API
    try {
      const payload = {
        ProductId: parseInt(Id),
        VariationId: selectedVariation.Id,
        Qty: quantity
      };
      const token = JSON.parse(sessionStorage.getItem("AuthToken"));
      const response = await AddToCart(payload, token);
      if (response.status) {
        toast.success(response.message || "Product added to cart successfully!");
        refreshCartCount(); // Refresh cart count after successful add
      } else {
        toast.error(response.message || "Failed to add product to cart.");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("An error occurred while adding to cart.");
    } finally {
      setIsAddingToCart(false); // ✅ hide loader
    }
  };

  // Show loading spinner while main product is loading
  if (loading) {
    return (
      <div>
        <Breadcrumb title="Product Details" path="Product Details" />
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-theme2"></div>
        </div>
      </div>
    );
  }

  // Show "Product not found" if product is null after loading
  if (!product) {
    return (
      <div>
        <Breadcrumb title="Product Details" path="Product Details" />
        <div className="container mx-auto px-4 py-10 text-center">
          <div className="py-8">Product not found</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isAddingToCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <PuffLoader color={"#83442B"} size={60} />
        </div>
      )}
      <Breadcrumb title="Product Details" path="Product Details" />

      <div className="container mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* LEFT - IMAGE SLIDER */}
        <div className="">
          <Swiper
            loop={true}
            spaceBetween={10}
            navigation={{
              nextEl: ".custom-next",
              prevEl: ".custom-prev",
            }}
            autoplay={{
              delay: 2500,
              disableOnInteraction: false,
            }}
            thumbs={{ swiper: thumbsSwiper }}
            modules={[FreeMode, Navigation, Thumbs, Autoplay]}
            className="relative w-full h-[350px] md:h-[450px] rounded-md shadow-md"
          >
            {[product.ThumbImage, ...product.Images].map((img, index) => (
              <SwiperSlide key={index}>
                <img
                  src={img}
                  alt={`${product.Name} ${index}`}
                  className="w-full h-full object-cover rounded-md"
                />
              </SwiperSlide>
            ))}
            <button className="custom-prev absolute top-1/2 left-6 transform -translate-y-1/2 bg-theme2 text-white p-2 rounded-full shadow hover:bg-black z-10">
              ❮
            </button>
            <button className="custom-next absolute top-1/2 right-6 transform -translate-y-1/2 bg-theme2 text-white p-2 rounded-full shadow hover:bg-black z-10">
              ❯
            </button>
          </Swiper>
          <Swiper
            onSwiper={setThumbsSwiper}
            loop={true}
            spaceBetween={10}
            slidesPerView={4}
            freeMode={true}
            watchSlidesProgress={true}
            modules={[FreeMode, Navigation, Thumbs]}
            className="mt-4"
          >
            {[product.ThumbImage, ...product.Images].map((img, index) => (
              <SwiperSlide key={index}>
                <img
                  src={img}
                  alt={`Thumbnail ${index}`}
                  className="w-full h-20 object-cover rounded cursor-pointer border hover:border-theme2"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* RIGHT - PRODUCT DETAILS */}
        <div>
          <div className="loader-line"></div>
          <div className="flex justify-between items-center">
            <h2 className="md:text-4xl text-xl font-bold text-darktext mt-3 pb-2 mb-2">
              {product.Name}
            </h2>
            <button onClick={handleShare} title="Share Product" className="bg-white border border-theme2 rounded p-1 hover:bg-theme2 hover:text-white transition">
              <IoShareSocial className="text-theme2 hover:text-white" />
            </button>
          </div>
          <p className="text-contentcolor mb-4" dangerouslySetInnerHTML={{ __html: product.Description }} />

          <div className="md:text-3xl text-xl mb-4 text-theme2 flex items-center">
            {currencySymbol}
            <span className="text-theme2 font-bold">
              {selectedVariation?.SalePrice}
              {selectedVariation?.RegularPrice && (
                <span className="line-through text-contentcolor mr-2 ml-2">
                  {selectedVariation.RegularPrice}
                </span>
              )}/- {selectedVariation?.Name || 'Pcs'}
            </span>
          </div>

          <div className="flex items-center justify-between text-contentcolor border-t border-b py-3">
            <span className="font-medium">Calories:</span>
            <span className="text-contentcolor flex items-center">
              <FaFireAlt className="mr-1" /> {product.Calories}
            </span>
          </div>


          <div className="flex items-center justify-between text-contentcolor border-b py-3">
            <span className="font-medium">Packaging:</span>
            <select
              className="border md:text-base text-sm rounded-md px-4 py-2 text-contentcolor max-w-full"
              onChange={handleVariationChange}
              value={selectedVariation?.Id}
            >
              {product.Variations.map((variation) => (
                <option key={variation.Id} value={variation.Id}>
                  {variation.Name} - Starting From {currencySymbol} {variation?.SalePrice}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between border-b py-3 text-contentcolor">
            <span className="font-medium">Quantity:</span>
            <div className="flex items-center">
              <button
                onClick={decreaseQty}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-l"
              >
                -
              </button>
              <input
                type="text"
                value={quantity}
                readOnly
                className="w-12 py-1 text-center border-t border-b"
              />
              <button
                onClick={increaseQty}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-r"
              >
                +
              </button>
            </div>
          </div>

          <button
            onClick={handleAddToCart}
            className="mt-6 w-full bg-theme2 hover:bg-black text-white py-3.5 rounded-full flex items-center justify-center space-x-2 transition-colors duration-300 font-medium"
          >
            <LuShoppingCart /> <span>Add To Cart</span>
          </button>
        </div>
      </div>


    </div>
  );
};

export default ProductDetails;

// import React, { useState, useEffect, useCallback } from "react";
// import { useParams } from "react-router-dom";
// import Breadcrumb from "../components/Breadcrumb";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Autoplay, FreeMode, Navigation, Thumbs } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/free-mode";
// import "swiper/css/navigation";
// import "swiper/css/thumbs";
// import { FaChevronLeft, FaChevronRight, FaFireAlt, FaRegBookmark, FaShare, FaDollarSign, FaRupeeSign } from "react-icons/fa";
// import { LuShoppingCart } from "react-icons/lu";
// import { IoShareSocial } from "react-icons/io5";
// import { Link } from "react-router-dom";
// import { getProductById, getAllProduct } from "../API/ProductAPI";
// import Config from "../API/Config";

// const ProductDetails = () => {
//   const { Id } = useParams();
//   const [product, setProduct] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [thumbsSwiper, setThumbsSwiper] = useState(null);
//   const [quantity, setQuantity] = useState(1);
//   const [selectedVariation, setSelectedVariation] = useState(null);

//   const StateId = Config.getStateId();
//   const CountryId = Config.getCountryId();
//   const currencySymbol = CountryId === 'IN' ? <FaRupeeSign /> : <FaDollarSign />;

//   // Flatten API response for related products
//   const flattenProducts = useCallback((data) => {
//     return data.flatMap(category =>
//       category.Products.map(product => ({
//         ...product,
//         category: category.Name
//       }))
//     );
//   }, []);

//   // Fetch product details and related products
//   useEffect(() => {
//     const fetchData = async () => {
//       setLoading(true);
//       try {
//         // Fetch product by ID
//         const productRes = await getProductById(Id, CountryId, StateId);
//         if (productRes.status) {
//           setProduct(productRes.responseData);
//           setSelectedVariation(productRes.responseData.Variations[0]);
//         }

//         // Fetch related products
//         const allProductsRes = await getAllProduct(StateId, CountryId);
//         const flattenedProducts = flattenProducts(allProductsRes);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [Id, StateId, CountryId, flattenProducts]);

//   const increaseQty = () => setQuantity(quantity + 1);
//   const decreaseQty = () => setQuantity(quantity > 1 ? quantity - 1 : 1);

//   const handleVariationChange = (e) => {
//     const variationId = parseInt(e.target.value);
//     const variation = product.Variations.find(v => v.Id === variationId);
//     setSelectedVariation(variation);
//   };

//   // Share functionality
//   const handleShare = async () => {
//     const shareData = {
//       title: product.Name,
//       text: product.Description.replace(/<[^>]+>/g, ""), // Strip HTML tags from description
//       url: window.location.href, // Current product page URL
//     };

//     try {
//       if (navigator.share) {
//         // Use Web Share API if available
//         await navigator.share(shareData);
//         console.log("Product shared successfully!");
//       } else {
//         // Fallback: Copy URL to clipboard
//         await navigator.clipboard.writeText(shareData.url);
//         alert("Product URL copied to clipboard!");
//       }
//     } catch (error) {
//       console.error("Error sharing product:", error);
//     }
//   };
//   if (!product) {
//     return <div className="text-center py-8">Product not found</div>;
//   }

//   return (
//     <div>
//       <Breadcrumb title="Product Details" path="Product Details" />

//       {loading ? (
//         <div className="flex justify-center items-center h-screen">
//           <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-theme2"></div>
//         </div>
//       ) : (
//         <div className="container mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
//           {/* LEFT - IMAGE SLIDER */}
//           <div className="">
//             <Swiper
//               loop={true}
//               spaceBetween={10}
//               navigation={{
//                 nextEl: ".custom-next",
//                 prevEl: ".custom-prev",
//               }}
//               autoplay={{
//                 delay: 2500,
//                 disableOnInteraction: false,
//               }}
//               thumbs={{ swiper: thumbsSwiper }}
//               modules={[FreeMode, Navigation, Thumbs, Autoplay]}
//               className="relative w-full h-[350px] md:h-[450px] rounded-md shadow-md"
//             >
//               {[product.ThumbImage, ...product.Images].map((img, index) => (
//                 <SwiperSlide key={index}>
//                   <img
//                     src={img}
//                     alt={`${product.Name} ${index}`}
//                     className="w-full h-full object-cover rounded-md"
//                   />
//                   {/* <span className="absolute top-3 right-3 bg-theme2 text-white text-sm px-2 py-1 rounded">
//                     ★ {product.rating || '3.2'}
//                   </span> */}
//                 </SwiperSlide>
//               ))}
//               <button className="custom-prev absolute top-1/2 left-6 transform -translate-y-1/2 bg-theme2 text-white p-2 rounded-full shadow hover:bg-black z-10">
//                 ❮
//               </button>
//               <button className="custom-next absolute top-1/2 right-6 transform -translate-y-1/2 bg-theme2 text-white p-2 rounded-full shadow hover:bg-black z-10">
//                 ❯
//               </button>
//             </Swiper>
//             <Swiper
//               onSwiper={setThumbsSwiper}
//               loop={true}
//               spaceBetween={10}
//               slidesPerView={4}
//               freeMode={true}
//               watchSlidesProgress={true}
//               modules={[FreeMode, Navigation, Thumbs]}
//               className="mt-4"
//             >
//               {[product.ThumbImage, ...product.Images].map((img, index) => (
//                 <SwiperSlide key={index}>
//                   <img
//                     src={img}
//                     alt={`Thumbnail ${index}`}
//                     className="w-full h-20 object-cover rounded cursor-pointer border hover:border-theme2"
//                   />
//                 </SwiperSlide>
//               ))}
//             </Swiper>
//           </div>

//           {/* RIGHT - PRODUCT DETAILS */}
//           <div>
//             <div className="loader-line"></div>
//             <div className="flex justify-between items-center">
//               <h2 className="md:text-4xl text-xl font-bold text-darktext mt-3 pb-2 mb-2">
//                 {product.Name}
//               </h2>
//               <button onClick={handleShare} title="Share Product" className="bg-white border border-theme2 rounded p-1 hover:bg-theme2 hover:text-white transition">
//                 <IoShareSocial className="text-theme2 hover:text-white" />
//               </button>
//             </div>
//             <p className="text-contentcolor mb-4" dangerouslySetInnerHTML={{ __html: product.Description }} />

//             <div className="md:text-3xl text-xl mb-4 text-contentcolor flex items-center">
//               {currencySymbol}
//               {selectedVariation?.Prices?.RegularPrice && (
//                 <span className="line-through text-contentcolor mr-2">
//                   {selectedVariation.Prices.RegularPrice}
//                 </span>
//               )}
//               <span className="text-theme2 font-bold">
//                 {selectedVariation?.Prices?.SalePrice} /- {selectedVariation?.Name || 'Pcs'}
//               </span>
//             </div>

//             <div className="flex items-center justify-between text-contentcolor border-t border-b py-3">
//               <span className="font-medium">Calories:</span>
//               <span className="text-contentcolor flex items-center">
//                 <FaFireAlt className="mr-1" /> {product.Calories}
//               </span>
//             </div>
//             <div className="flex items-center justify-between text-contentcolor border-t border-b py-3">
//               <span className="font-medium">Category:</span>
//               <span className="text-contentcolor flex items-center">
//                 {product.Category.Name}
//               </span>
//             </div>

//             <div className="flex items-center justify-between text-contentcolor border-b py-3">
//               <span className="font-medium">Packaging:</span>
//               <select
//                 className="border md:text-base text-sm rounded-md px-4 py-2 text-contentcolor max-w-full"
//                 onChange={handleVariationChange}
//                 value={selectedVariation?.Id}
//               >
//                 {product.Variations.map((variation) => (
//                   <option key={variation.Id} value={variation.Id}>
//                     {variation.Name} - Starting From {currencySymbol} {variation.Prices.SalePrice}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div className="flex items-center justify-between border-b py-3 text-contentcolor">
//               <span className="font-medium">Quantity:</span>
//               <div className="flex items-center">
//                 <button
//                   onClick={decreaseQty}
//                   className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-l"
//                 >
//                   -
//                 </button>
//                 <input
//                   type="text"
//                   value={quantity}
//                   readOnly
//                   className="w-12 py-1 text-center border-t border-b"
//                 />
//                 <button
//                   onClick={increaseQty}
//                   className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-r"
//                 >
//                   +
//                 </button>
//               </div>
//             </div>

//             <button className="mt-6 w-full bg-theme2 hover:bg-black text-white py-3.5 rounded-full flex items-center justify-center space-x-2 transition">
//               <LuShoppingCart /> <span>Add To Cart</span>
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ProductDetails;

